import DynamicFieldsFragment from '@linc-technologies/linced-common/models/dynamic-field/base-fragment';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragment } from 'ember-data-model-fragments/attributes';
import type BulkImportCaregiver from 'linced-app/models/people/bulk-import-caregiver';
import type BulkImportMedical from 'linced-app/models/people/bulk-import-medical';

// Proto: people.BulkImportContact.
export default class BulkImportContact extends DynamicFieldsFragment {
  declare [Type]: 'people/bulk-import-contact';

  // type specifies what responsibilities the contact holds with respect to
  // the person.
  @attr('number', { defaultValue: 0 })
  declare type: number;

  // priority determines the order in which contacts are displayed.
  // priority has no default: controllers/admin/import/index.js:1675 uses
  // isEmpty to set the priority from the column name.
  @attr('number', { defaultValue: 0 })
  declare priority: number;

  // Relationship is the type of relationship of the contact with respect to
  // the person.
  @attr('number', { defaultValue: 0 })
  declare relationship: number;

  // Notes regarding the contact.
  @attr('string', { defaultValue: '' })
  declare notes: string;

  // allowedAccess specifies whether the caregiver has access to the
  // learner's record.
  @attr('boolean', { defaultValue: false })
  declare allowedAccess: boolean;

  // receivesSms specifies whether the contact receives text (SMS)
  // notifications for this Learner.
  @attr('boolean', { defaultValue: false })
  declare receivesSms: boolean;

  // billPayer specifies whether the contact pays bills for this Learner.
  @attr('boolean', { defaultValue: false })
  declare billPayer: boolean;

  // propsToImport specifies which of the above properties to import.
  // This is to work around the ambiguity of the empty value (are we
  // importing an empty value, or do we want to leave it unchanged?).
  @array('number')
  declare propsToImport: StatefulArray<number>;

  // caregiver specifies the details of the Caregiver who this Contact
  // record relates to.
  @fragment('people/bulk-import-caregiver')
  declare caregiver: BulkImportCaregiver | null;

  // medical specifies the details of the Medical Specialist who this Contact
  // record relates to.
  @fragment('people/bulk-import-medical')
  declare medical: BulkImportMedical | null;
  // dynamic fields to do with the relationship (rather than the caregiver).
  // `relationshipFields` is handled by the DynamicFields serializer (it
  // maps the dynamic fields to/from `relationshipFields`).
}
